import React, {useEffect, useState} from "react";
import Layout from "../Layout";
import {Button, Card, Descriptions, Form, Input, message, Modal, Space, Table,} from "antd";
import {FaEye, FaEyeSlash} from "react-icons/fa";

const StudentView = () => {
    const [students, setStudents] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    // Add Student Modal
    const [addModalVisible, setAddModalVisible] = useState(false);

    // Detail Modal
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);

    // Create new student data
    const [newStudentData, setNewStudentData] = useState({
        name: "",
        email: "",
        password: "",
        role: "student",
    });
    const [passwordShown, setPasswordShown] = useState(false);

    // Constants
    const server_url = "https://api.edumonics.in";
    const accessToken = localStorage.getItem("accessToken");

    // Toggle password visibility
    const togglePasswordVisibility = () =>
        setPasswordShown((prev) => !prev);

    // Filter/Searching
    const handleSearch = (e) => {
        setSearchTerm(e.target.value.toLowerCase());
    };

    // Filter students by name, email, or role
    const filteredStudents = students.filter(
        (s) =>
            (s.name && s.name.toLowerCase().includes(searchTerm)) ||
            (s.email && s.email.toLowerCase().includes(searchTerm)) ||
            (s.role && s.role.toLowerCase().includes(searchTerm)) ||
            (s.class_grade && s.class_grade.toLowerCase().includes(searchTerm)) ||
            (s.school_name && s.school_name.toLowerCase().includes(searchTerm))
    );

    // Fetch students from /admin/get-students
    const fetchStudents = async () => {
        try {
            const response = await fetch(`${server_url}/admin/get-students`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to fetch students");
            }
            const data = await response.json();
            setStudents(data);
        } catch (error) {
            console.error("Error fetching students:", error);
            message.error(error.message || "Error fetching students.");
        }
    };

    useEffect(() => {
        fetchStudents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Create new student
    const handleCreateStudent = async () => {
        try {
            const response = await fetch(`${server_url}/admin/create-user`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(newStudentData),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || "Failed to create student");
            }
            message.success(data.message || "Student created successfully!");
            setAddModalVisible(false);
            fetchStudents();
        } catch (error) {
            console.error("Error creating student:", error);
            message.error(error.message || "Error creating student.");
        }
    };

    // Delete student
    const deleteStudent = async (email) => {
        const confirmed = window.confirm(
            "Are you sure you want to delete this student?"
        );
        if (!confirmed) return;

        try {
            const response = await fetch(`${server_url}/admin/delete-user/${email}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || "Failed to delete student");
            }
            message.success(data.detail);
            setStudents((prev) => prev.filter((student) => student.email !== email));
        } catch (error) {
            console.error("Error deleting student:", error);
            message.error(error.message || "Error deleting student.");
        }
    };

    // Table columns
    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (text) => text || "--",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            render: (text) => text || "--",
        },
        {
            title: "Role",
            dataIndex: "role",
            key: "role",
            render: (text) => text || "--",
        },
        {
            title: "School",
            dataIndex: "school_name",
            key: "school_name",
            render: (text) => text || "--",
        },
        {
            title: "Class Grade",
            dataIndex: "class_grade",
            key: "class_grade",
            render: (text) => text || "--",
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (val) =>
                val ? new Date(val).toLocaleString() : "--",
        },
        {
            title: "Action",
            key: "action",
            render: (student) => (
                <Space>
                    <Button
                        type="primary"
                        onClick={() => {
                            setSelectedStudent(student);
                            setDetailModalVisible(true);
                        }}
                        className=" bg-yellow-400 hover:bg-yellow-500 text-black p-2 rounded-md"
                    >
                        View
                    </Button>
                    <Button
                        type="text"
                        danger
                        onClick={() => deleteStudent(student.email)}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    // RENDER
    return (
        <Layout pageTitle="View Students">
            <div className="mx-auto px-6 py-8">
                <h1 className="text-3xl font-semibold text-center font-gotham text-gray-800 mb-6">
                    Student Accounts
                </h1>

                {/* Search Input */}
                <div className="flex justify-center mb-4">
                    <Input
                        placeholder="Search by Name, Email, Role, or Class..."
                        style={{width: "50%",fontFamily: "Gotham, sans-serif"}}
                        onChange={handleSearch}
                    />
                </div>

                {/* Create Student Button */}
                <div className="flex justify-end mb-4">
                    <Button
                        type="primary"
                        onClick={() => setAddModalVisible(true)}
                        className=" bg-yellow-400 hover:bg-yellow-500 text-black p-2 rounded-md"

                    >
                        Create New Student
                    </Button>
                </div>

                {/* Student Table */}
                <Table
                    dataSource={filteredStudents}
                    columns={columns}
                    rowKey="id"
                    bordered
                />

                {/* Modal: Add Student */}
                <Modal
                    title="Add New Student"
                    open={addModalVisible}
                    onCancel={() => setAddModalVisible(false)}
                    footer={null}
                    destroyOnClose
                >
                    <Form
                        layout="vertical"
                        onFinish={handleCreateStudent}
                    >
                        <Form.Item
                            label="Name"
                            required
                            tooltip="Enter the student's full name"
                        >
                            <Input
                                name="name"
                                value={newStudentData.name}
                                onChange={(e) =>
                                    setNewStudentData({
                                        ...newStudentData,
                                        name: e.target.value,
                                    })
                                }
                                placeholder="John Doe"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Email"
                            required
                            tooltip="Student's email address"
                        >
                            <Input
                                type="email"
                                name="email"
                                value={newStudentData.email}
                                onChange={(e) =>
                                    setNewStudentData({
                                        ...newStudentData,
                                        email: e.target.value,
                                    })
                                }
                                placeholder="johndoe@example.com"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Password"
                            required
                            tooltip="Set an initial password"
                        >
                            <div style={{position: "relative",fontFamily: "Gotham, sans-serif",}}>
                                <Input
                                    type={passwordShown ? "text" : "password"}
                                    name="password"
                                    value={newStudentData.password}
                                    onChange={(e) =>
                                        setNewStudentData({
                                            ...newStudentData,
                                            password: e.target.value,
                                        })
                                    }
                                    placeholder="••••••••"
                                />
                                <span
                                    style={{
                                        position: "absolute",
                                        right: 10,
                                        top: "30%",
                                        cursor: "pointer",
                                        fontFamily: "Gotham, sans-serif",
                                    }}
                                    onClick={togglePasswordVisibility}
                                >
                  {passwordShown ? <FaEye/> : <FaEyeSlash/>}
                </span>
                            </div>
                        </Form.Item>

                        <Form.Item label="Role">
                            <Input
                                value="student"
                                disabled
                            />
                        </Form.Item>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" block
                                    className=" bg-yellow-400 hover:bg-yellow-500 text-black p-2 rounded-md"
                            >
                                Add Student
                            </Button>
                        </Form.Item>
                    </Form>
                </Modal>

                {/* Modal: Student Details */}
                <Modal
                    title="Student Details"
                    open={detailModalVisible}
                    onCancel={() => setDetailModalVisible(false)}
                    footer={null}
                    destroyOnClose
                    width={800}
                >
                    {selectedStudent && (
                        <Card
                            bordered={false}
                            style={{maxHeight: "70vh", overflowY: "auto",fontFamily: "Gotham, sans-serif",}}
                        >
                            <Descriptions
                                layout="vertical"
                                column={{xs: 1, sm: 2, md: 2}}
                                bordered
                                size="small"
                                labelStyle={{fontWeight: "bold", width: 150,fontFamily: "Gotham, sans-serif",}}
                            >
                                {/* Basic Info */}
                                <Descriptions.Item label="Name">
                                    {selectedStudent.name || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Email">
                                    {selectedStudent.email || "--"}
                                </Descriptions.Item>

                                <Descriptions.Item label="Role">
                                    {selectedStudent.role || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Created At">
                                    {selectedStudent.created_at
                                        ? new Date(selectedStudent.created_at).toLocaleString()
                                        : "--"}
                                </Descriptions.Item>

                                {/* Student Profile Fields */}
                                <Descriptions.Item label="School Name">
                                    {selectedStudent.school_name || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="UDISE Code">
                                    {selectedStudent.udise_code || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Teacher Code">
                                    {selectedStudent.teacher_code || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Apaar ID">
                                    {selectedStudent.apaar_id || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Roll No">
                                    {selectedStudent.roll_no || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Registration No">
                                    {selectedStudent.registration_no || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Class Grade">
                                    {selectedStudent.class_grade || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Section">
                                    {selectedStudent.section || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Date of Birth">
                                    {selectedStudent.date_of_birth || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Address">
                                    {selectedStudent.address || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Phone">
                                    {selectedStudent.phone || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Mother's Name">
                                    {selectedStudent.mother_name || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Mother's Education">
                                    {selectedStudent.mother_education || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Mother's Occupation">
                                    {selectedStudent.mother_occupation || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Father's Name">
                                    {selectedStudent.father_name || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Father's Education">
                                    {selectedStudent.father_education || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Father's Occupation">
                                    {selectedStudent.father_occupation || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Number of Siblings">
                                    {selectedStudent.number_of_siblings || 0}
                                </Descriptions.Item>
                                <Descriptions.Item label="Siblings' Ages">
                                    {selectedStudent.siblings_age &&
                                    selectedStudent.siblings_age.length
                                        ? selectedStudent.siblings_age.join(", ")
                                        : "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Mother Tongue">
                                    {selectedStudent.mother_tongue || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Medium of Instruction">
                                    {selectedStudent.medium_of_instruction || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Rural/Urban">
                                    {selectedStudent.rural_or_urban || "--"}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    )}
                </Modal>
            </div>
        </Layout>
    );
};

export default StudentView;
